import { clientManifestSchema, type ClientManifest } from "@shared/contracts";
import { preferences } from "@/app/platform";

/**
 * WHAT THIS BUILD SHOULD BE DOING, ACCORDING TO THE SERVICE — AND WHAT IT DOES WHEN THE
 * SERVICE HAS NOTHING TO SAY.
 *
 * This file owns the values and the fallbacks. The fetching lives next door in
 * `app/lib/manifest.ts`, and the split is not tidiness: everything here has to be readable
 * from `app/observability`, which `app/lib/api-client.ts` already reports through. Keeping
 * the network out of this module is what keeps that one-directional.
 *
 * ## The property everything else rests on: a manifest can never make things worse
 *
 * Every value below has a hard-coded default that means "carry on exactly as you would with
 * no manifest at all". The service being down, slow, unreachable behind a captive portal, or
 * answering something this app cannot parse are all the same case, and in every one of them
 * the app runs as it does today. Nothing here is ever awaited before the app starts.
 *
 * That is not a nicety. A remote switch that can strand an install is a worse liability than
 * the problem it solves — the builds it would strand are the ones nobody can recall, and the
 * only fix would be the App Store release this feature exists to avoid needing.
 *
 * ## Read at the point of use, never copied at start-up
 *
 * `getManifest()` is called where the value is needed, every time. Copying these into a
 * store at launch would mean an answer that changes mid-session changes nothing until the
 * user relaunches — and on a phone that can be days.
 */

/**
 * THE DEFAULTS, AND EVERY ONE OF THEM IS THE APP'S BEHAVIOUR TODAY.
 *
 * If you add a field, the question is not "what is a sensible value" but "what value makes
 * this indistinguishable from the app before the field existed".
 */
export const PERMISSIVE_MANIFEST: ClientManifest = {
  /**
   * A floor of 0.0.0 blocks nothing and recommends nothing, so an unreachable service can
   * never produce a wall. `checkVersion` has always answered "ok" when it could not ask;
   * this is the same decision, moved to where every value now lives.
   */
  versionFloor: {
    minimum: "0.0.0",
    recommended: "0.0.0",
    storeUrl: { ios: "", android: "", web: "" },
  },
  /** Everything is emitted, which is what `log.*` did before there was a threshold. */
  logLevel: "debug",
  replay: { sessionSampleRate: 0, onErrorSampleRate: 1 },
  featureFlags: {},
  /** Off. A channel that turns itself on when the kill switch is unreachable is not one. */
  ota: { enabled: false, minimumBundleVersion: "0.0.0" },
  supportUrl: "",
};

/**
 * ⚠️ A POLICY LIMIT, NOT A SETTING — AND THE REASON IT IS ENFORCED HERE.
 *
 * Session replay in this app is record-on-error only: nothing is recorded until something
 * breaks, at which point the buffered window is attached to the error. The whole privacy
 * position rests on that — disclosure rather than consent is defensible for error
 * diagnostics and is not defensible for continuously recording people using the app.
 *
 * A server value that can flip that is a server value that can silently invalidate the
 * position, which is why the client refuses it rather than trusting the deploy that sets it.
 * The service defaults it to zero too; this is the wall that actually holds, because it
 * holds against a service that is compromised, misconfigured, or simply somebody else's.
 *
 * Raising this is a deliberate act with a legal component. It is not a config change.
 */
export const MAX_REPLAY_SESSION_SAMPLE_RATE = 0;

/**
 * HOW OLD A CACHED ANSWER MAY BE BEFORE IT IS IGNORED.
 *
 * Past this the cached copy is not merely stale, it is untrustworthy: the phone has been
 * unable to reach the service for a day, and a value set for last week's incident should
 * not still be steering the app. The fallback is the permissive defaults — never nothing,
 * and never the old answer.
 */
export const MANIFEST_IGNORED_AFTER_MS = 24 * 60 * 60 * 1000;

/**
 * Namespaced like the other entry in this store (`app.query-cache`). Written through
 * `preferences`, which is `UserDefaults` on a phone: durable, not swept under storage
 * pressure, and — unlike the Keychain next door — the right place for something that is not
 * a credential. Nothing user-identifying is in here.
 */
export const MANIFEST_CACHE_KEY = "app.client-manifest";

type CachedManifest = { fetchedAt: number; manifest: ClientManifest };

/** Where the values in force came from. `defaults` means nothing has been read yet. */
export type ManifestSource = "defaults" | "cache" | "network";

let current: ClientManifest = PERMISSIVE_MANIFEST;
let source: ManifestSource = "defaults";

/**
 * THE VALUES IN FORCE RIGHT NOW. Never throws, never returns undefined, never blocks.
 *
 * Callers read a field off this at the moment they need it. `log.*` in app/observability
 * does exactly that, which is what makes a log level that changes mid-session take effect
 * on the next line rather than the next launch.
 */
export function getManifest(): ClientManifest {
  return current;
}

/**
 * WHERE THE CURRENT ANSWER CAME FROM, WHICH IS THE STALENESS FLAG.
 *
 * `stale` means "nothing has been heard from the service this session" — the values are a
 * cached copy or the built-in defaults. It is deliberately not a field on the manifest
 * itself: the manifest is what the service said, and this is a fact about the app.
 */
export function getManifestStatus(): { source: ManifestSource; stale: boolean } {
  return { source, stale: source !== "network" };
}

/**
 * PARSE, NEVER CAST — and a partial or garbled answer is refused whole.
 *
 * Zod strips what the contract does not declare and refuses what it declares wrongly, so a
 * body that has drifted, been truncated by a proxy, or is actually a captive portal's login
 * page produces `null` here and the caller keeps whatever it already had. There is no
 * field-by-field salvage on purpose: half a manifest is a state nobody reasoned about, and
 * the defaults are a state everybody did.
 */
export function parseManifest(raw: unknown): ClientManifest | null {
  const parsed = clientManifestSchema.safeParse(raw);
  if (!parsed.success) return null;
  return applyPolicyLimits(parsed.data);
}

/**
 * The cap, applied on the way in rather than at the point of use, so that NO caller can read
 * an uncapped value — including one written later by somebody who never read this file.
 */
function applyPolicyLimits(manifest: ClientManifest): ClientManifest {
  return {
    ...manifest,
    replay: {
      ...manifest.replay,
      sessionSampleRate: Math.min(manifest.replay.sessionSampleRate, MAX_REPLAY_SESSION_SAMPLE_RATE),
    },
  };
}

/**
 * Adopt a manifest fetched from the service, and keep a copy for the next cold launch.
 *
 * The write is not awaited by the caller's critical path and a failure to store is not a
 * failure to apply: the values are already in force in memory, and the only thing lost is a
 * head start on the next launch.
 */
export async function adoptManifest(manifest: ClientManifest): Promise<void> {
  current = manifest;
  source = "network";
  const entry: CachedManifest = { fetchedAt: Date.now(), manifest };
  await preferences.set(MANIFEST_CACHE_KEY, JSON.stringify(entry)).catch(() => {
    // Out of room, or a store that refuses writes. Nothing to tell the user and nothing to
    // fix: this launch is unaffected and the next one starts from the defaults.
  });
}

/**
 * THE LAST GOOD ANSWER, READ BEFORE ANYTHING TOUCHES THE NETWORK.
 *
 * A cold launch is most launches on a phone — iOS reclaims a backgrounded app whenever it
 * wants the memory — so without this the app would spend the first seconds of every launch
 * on the defaults while a round trip this project budgets at four seconds runs. That window
 * is exactly when start-up work reads these values.
 *
 * Three things it will not do: block (one local read, no network), throw (every failure ends
 * on the defaults), or overwrite a fresher answer. The last one is a real race and not a
 * theoretical one — the fetch is started in the same tick — so a network answer that got
 * there first wins, deterministically, rather than by being slower than a disk read.
 */
export function primeManifestFromCache(): Promise<void> {
  // Memoised, so the two callers that both need it before they can start — reporting, which
  // fixes the replay rates at init, and the watcher, which must not run on the defaults —
  // share one read instead of racing each other for the same key.
  priming ??= readCachedManifest();
  return priming;
}

let priming: Promise<void> | null = null;

async function readCachedManifest(): Promise<void> {
  const raw = await preferences.get(MANIFEST_CACHE_KEY).catch(() => null);
  if (!raw) return;

  let entry: unknown;
  try {
    entry = JSON.parse(raw);
  } catch {
    // Someone else's key, a half-written value, a store that returned nonsense. The defaults
    // are already in force and that is the correct answer here.
    return;
  }

  if (typeof entry !== "object" || entry === null) return;
  const { fetchedAt, manifest } = entry as Partial<CachedManifest>;
  if (typeof fetchedAt !== "number") return;

  // A clock that moved backwards (a timezone change, a manual set) makes the age negative
  // rather than large, so guard both ends: an entry from the future is as untrustworthy as
  // one from last week.
  const age = Date.now() - fetchedAt;
  if (age < 0 || age > MANIFEST_IGNORED_AFTER_MS) return;

  const parsed = parseManifest(manifest);
  if (!parsed) return;

  // Checked AFTER the read, not before it: the fetch is started in the same tick, and on a
  // fast connection it can answer while this is still waiting on the store. A cached copy
  // must never displace one that came from the service.
  if (source === "network") return;

  current = parsed;
  source = "cache";
}
